import React from "react";

/**
 * PatientAvatar — shows the patient's profile photo (`photo_data_url`)
 * when set, falls back to an initials circle otherwise. Used wherever
 * we render a roster of patients (My Patients list, patient picker
 * dropdowns, case detail patient headers, etc.).
 *
 * Why a component: before this, the doctor's "My Patients" rendered
 * `initials(p.name)` directly in a circle, silently ignoring the
 * `photo_data_url` field even when patients had uploaded a real photo
 * via /patient/me/photo. Centralising the rendering ensures any future
 * roster view picks up the photo for free.
 *
 * Props:
 *   patient   — the patient dict from the API (must have `photo_data_url`
 *               optional + `name`). Pass the WHOLE patient so future
 *               additions (status dots, online badge, etc.) only need
 *               this component edited.
 *   size      — "sm" (28px) | "md" (36px, default) | "lg" (44px) | "xl" (64px).
 *               These match the existing roster size variations.
 *   className — extra Tailwind classes (e.g. ring colours when selected).
 *
 * No data fetching here — purely presentational. The list pages already
 * have the patient dict in hand from /doctor/patients.
 */
const SIZE = {
    sm: { box: "w-7 h-7", text: "text-[10px]" },
    md: { box: "w-9 h-9", text: "text-sm" },
    lg: { box: "w-11 h-11", text: "text-base" },
    xl: { box: "w-16 h-16", text: "text-xl" },
};

function initials(name) {
    return (name || "?")
        .split(/\s+/)
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

export function PatientAvatar({ patient, size = "md", className = "" }) {
    const s = SIZE[size] || SIZE.md;
    // Accept both `photo_data_url` (patient self-upload, base64) and `image`
    // (URL avatar — used by the demo seed + future avatar service). An <img>
    // tag handles either kind transparently.
    const photo = patient?.photo_data_url || patient?.image;
    const ringCls = `${s.box} rounded-full shrink-0 ${className}`;

    if (photo) {
        return (
            <img
                src={photo}
                alt={patient?.name || "Patient"}
                className={`${ringCls} object-cover border border-border bg-secondary`}
                data-testid={patient?.id ? `patient-avatar-${patient.id}` : "patient-avatar"}
                loading="lazy"
            />
        );
    }
    return (
        <div
            className={`${ringCls} bg-primary/10 text-primary font-semibold grid place-items-center ${s.text}`}
            data-testid={patient?.id ? `patient-avatar-${patient.id}` : "patient-avatar"}
            aria-label={`Initials for ${patient?.name || "patient"}`}
        >
            {initials(patient?.name)}
        </div>
    );
}

export default PatientAvatar;
